import { Button, cn, constructUriWithSearchParams } from "@shared/ui";
import { getAccessToken, updateIgnition } from "../lib/axle";

// External dependencies
import _, { get, isNil } from "lodash";
import { useEffect, useRef, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import Loader from "../components/Loader";
import PopGradient from "../components/PopGradient";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { SUCCESS_SUBHEADER } from "../constants";

const Success = ({
  step,
  policyInfo,
  session,
  client,
  loginInformation,
  posthog,
  nextStep,
}) => {
  const [isRedirectEnabled, setRedirectEnabled] = useState(
    session.redirectUri || session.origin,
  );

  // When confirmationBeforeRedirect is true, show the success message and wait for user to click before redirecting
  const confirmationBeforeRedirect = get(
    session,
    "config.confirmationBeforeRedirect",
    false,
  );

  // Track whether the async success flow has completed (so the confirmation button can be shown)
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);

  // Store redirect params so they can be used when the user clicks the confirmation button
  const redirectParamsRef = useRef(null);

  // Get login result from state
  const result = _(loginInformation).get("result");
  const resultDetail = _(loginInformation).get("resultDetail");

  useEffect(() => {
    posthog.capture("$pageview", {
      step,
      carrier: get(loginInformation, "carrier.id"),
      result: result,
    });
    const success = async () => {
      try {
        // Get auth code for session and account
        const { authCode, error: authCodeError } = await getAccessToken(
          [policyInfo.id], // Adding single policyId to array, to update when supporting multiple policies
          session.id,
        );

        if (authCodeError) {
          switch (get(authCodeError, "code")) {
            case 500:
              console.log(JSON.stringify(authCodeError));
              nextStep("axle-error");
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }

        if (isNil(authCode)) {
          // will be true if authCode is null or undefined
          console.error("authCode is null or undefined");
          nextStep("axle-error");
          return;
        }

        // Construct webhook event
        const lastEvent = {
          type: `ignition.completed`,
          data: {
            token: session.id,
            authCode,
            result,
            resultDetail,
            user: session.user,
            client: client.id,
          },
          createdAt: new Date().toISOString(),
        };

        // Update Ignition session
        const params = {
          status: "completed",
          lastEvent,
          result,
          resultDetail,
        };
        const [updateIgnitionResponse, updateIgnitionError] =
          await updateIgnition(session.id, params);

        if (updateIgnitionError) {
          switch (get(updateIgnitionError, "code")) {
            case 500:
              console.log(JSON.stringify(updateIgnitionError));
              nextStep("axle-error");
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }

        if (authCode && isRedirectEnabled) {
          if (confirmationBeforeRedirect) {
            // Store the authCode for later use when user clicks the confirmation button
            redirectParamsRef.current = authCode;
            setIsProcessingComplete(true);
          } else {
            await sendEventAndRedirect(authCode);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    trackPromise(success());
  }, [posthog]);

  const sendEventAndRedirect = async (authCodeParam) => {
    // Create params for postMessage or redirectUri
    const paramsToAdd = {
      status: "complete",
      authCode: authCodeParam,
      client: client.id,
      result,
    };

    // Send message to parent window if initialized in an iframe
    if (session.origin) {
      const sessionOrigin = session.origin;
      window.parent.postMessage(paramsToAdd, sessionOrigin);
      console.log(`Sending message to session origin ${session.origin}`);
      console.log(
        "Message sent to parent window",
        JSON.stringify(paramsToAdd, null, 2),
      );
    }

    if (session.redirectUri) {
      const constructedUri = constructUriWithSearchParams(
        session.redirectUri,
        paramsToAdd,
      );

      window.location.href = constructedUri;
    }
  };

  // Handler for the confirmation button click
  const handleConfirmRedirect = async () => {
    if (redirectParamsRef.current) {
      await sendEventAndRedirect(redirectParamsRef.current);
    }
  };

  const hasFooter = isRedirectEnabled && confirmationBeforeRedirect;
  // Determine whether to show the message content vs a loader
  const showMessageContent =
    !isRedirectEnabled || (confirmationBeforeRedirect && isProcessingComplete);

  return (
    <>
      {showMessageContent ? (
        <>
          <StepBody
            centered
            className={cn(
              "items-center gap-4 text-center",
              // Reserve a footer's height so the content sits as it would with one.
              !hasFooter && "pb-24"
            )}
          >
            <SuccessMark />
            <StepHeading
              align="center"
              title={get(session, "config.success.header") ?? "All set!"}
              description={
                get(session, "config.success.subheader") ?? SUCCESS_SUBHEADER
              }
            />
            {get(session, "config.success.primary.text", undefined) && (
              <p className="text-body-lg text-ink-secondary text-pretty">
                {get(session, "config.success.primary.text")}
              </p>
            )}
          </StepBody>
          {hasFooter && (
            <StepFooter>
              <Button
                onClick={handleConfirmRedirect}
                name="success-confirm-redirect"
              >
                Continue
              </Button>
            </StepFooter>
          )}
        </>
      ) : (
        <StepBody>
          <Loader isOpen={true} />
        </StepBody>
      )}
    </>
  );
};

const SuccessMark = () => (
  <span aria-hidden className="relative mb-4 flex size-20 items-center justify-center">
    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-live-start to-live-end opacity-0 motion-safe:animate-success-ring" />
    <span className="relative flex size-20 items-center justify-center rounded-full bg-ink text-on-primary motion-safe:animate-success-pop">
      <svg viewBox="0 0 24 24" fill="none" className="size-9">
        <PopGradient id="success-check-gradient" x1={5} y1={17} x2={19} y2={7.5} />
        <path
          d="M5 12.5l4.5 4.5L19 7.5"
          pathLength={1}
          stroke="url(#success-check-gradient)"
          strokeWidth={2.25}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={1}
          className="motion-safe:animate-success-check"
        />
      </svg>
    </span>
  </span>
);

export default Success;
